import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { EMPTY_ARRAY, useConfirmationDialog } from "@regrello/core-utils";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import {
  CustomFieldDefaultColumnOption,
  FieldType,
  PropertyDataType,
  type SpectrumFieldVersionFields,
  useLatestSpectrumFieldVersionsV2Query,
} from "@regrello/graphql-api";
import {
  RegrelloIcon,
  RegrelloTabsList,
  RegrelloTabsRoot,
  RegrelloTabsTrigger,
  RegrelloTypography,
} from "@regrello/ui-core";
import React, { useCallback, useMemo, useState } from "react";
import { useDebounce } from "react-use";

import { DEBOUNCE_TIMEOUT, WORKFLOW_OWNER_FIELD_NAME } from "../../../constants/globalConstants";
import { PAGINATION_LIMIT } from "../../../constants/numbers";
import { FeatureFlagService } from "../../../services/FeatureFlagService";
import { useErrorHandler } from "../../../utils/hooks/useErrorHandler";
import { RegrelloAddRoleDialog } from "../../views/modals/formDialogs/roles/RegrelloAddRoleDialog";
import { ConfigureSpectrumFieldDialog } from "../../views/modals/formDialogs/spectrumFields/ConfigureSpectrumFieldDialog";
import { SpectrumFieldPluginRegistrar } from "../spectrumFields/registry/spectrumFieldPluginRegistrar";
import type { SpectrumFieldPluginDecorator } from "../spectrumFields/types/SpectrumFieldPluginDecorator";
import { RegrelloFormFieldSelectOption } from "./_internal/selectOptions/RegrelloFormFieldSelectOption";
import { RegrelloFormFieldSelectOptionRegrelloObject } from "./_internal/selectOptions/RegrelloFormFieldSelectOptionRegrelloObject";
import { RegrelloSelectV2AddOption } from "./_internal/selectOptions/RegrelloSelectV2AddOption";
import { RegrelloSelectV2LoadingOption } from "./_internal/selectOptions/RegrelloSelectV2LoadingOption";
import { type RegrelloFormFieldSelectPropsV2, RegrelloFormFieldSelectV2 } from "./RegrelloFormFieldSelectV2";

enum FieldOrRoleSelectTabId {
  ALL = "all",
  FIELDS = "fields",
  ROLES = "roles",
}

export interface RegrelloFormFieldOrRoleSelectProps
  extends Omit<
    RegrelloFormFieldSelectPropsV2<SpectrumFieldVersionFields>,
    | "getOptionLabel"
    | "getSelectedItemProps"
    | "isItemsEqual"
    | "itemPredicate"
    | "items"
    | "options"
    | "renderItem"
    | "variant"
  > {
  /**
   * Defines whether the 'Add X' options are shown in each tab.
   */
  allowCreate?: {
    field?: boolean;
    roles?: boolean;
  };

  /**
   * Whether to allow selecting the workflow owner system field
   *
   * @default false
   */
  allowSelectWorkflowOwner?: boolean;

  /**
   * An allow list for filtering which field plugins this field should allow the user to select.
   */
  allowedFieldPlugins?: Array<SpectrumFieldPluginDecorator<unknown>>;

  /**
   * Describes which tabs should be hidden (`true`) or visible (`false`). By default, all tabs are
   * and should be visible (`false`).
   */
  hiddenTabs?: {
    fields?: boolean;
    roles?: boolean;
  };

  /**
   * A list of options that should not be displayed in the suggestions menu. Useful for preventing
   * already-selected fields from being selected again, for example.
   */
  omittedOptions?: SpectrumFieldVersionFields[];

  /** Ref to pass to the button/trigger element. */
  selectRef?: React.Ref<HTMLButtonElement>;

  /** Any provided tab strings will be used instead of the default tab names. */
  overrideTabNames?: {
    all?: string;
    fields?: string;
    roles?: string;
  };

  /** The currently selected value. */
  value: SpectrumFieldVersionFields | null;

  /**
   * The visual emphasis of the button (outline, ghost).
   *
   * @default "outline"
   */
  variant?: "ghost" | "outline";
}

/**
 * A select component allowing users to select either a Field or Role, with tabbed interface.
 */
export const RegrelloFormFieldOrRoleSelect = React.memo<RegrelloFormFieldOrRoleSelectProps>(
  function RegrelloFormFieldOrRoleSelectFn({
    allowCreate,
    allowedFieldPlugins,
    allowSelectWorkflowOwner,
    helperText,
    hiddenTabs: propsHiddenTabs,
    omittedOptions,
    onChange,
    overrideTabNames,
    value: propsValue,
    variant,
    ...formFieldSelectV2Props
  }) {
    const { handleError } = useErrorHandler();
    const isPermissionsV2Enabled = FeatureFlagService.isEnabled(FeatureFlagKey.PERMISSIONS_V2_2024_01);

    const fieldCreateDialog = useConfirmationDialog<string>();
    const roleCreateDialog = useConfirmationDialog<string>();

    const [selectedTabId, setSelectedTabId] = React.useState<FieldOrRoleSelectTabId>(FieldOrRoleSelectTabId.ALL);

    const isFieldsTabHidden = propsHiddenTabs?.fields === true;
    const isRolesTabHidden = !isPermissionsV2Enabled || propsHiddenTabs?.roles === true;
    // (wsheehan): Show the 'All' tab if there is more than one tab shown.
    const isAllTabShown = [isFieldsTabHidden, isRolesTabHidden].filter((hidden) => !hidden).length > 1;

    const hiddenTabs = useMemo(() => {
      return {
        all: !isAllTabShown,
        fields: isFieldsTabHidden,
        roles: isRolesTabHidden,
      };
    }, [isAllTabShown, isFieldsTabHidden, isRolesTabHidden]);

    const omittedOptionIds = useMemo(
      () =>
        new Set(
          (omittedOptions ?? EMPTY_ARRAY)
            .filter((option) => option.spectrumField.id !== propsValue?.spectrumField.id)
            .map((option) => option.spectrumField.id),
        ),
      [propsValue?.spectrumField.id, omittedOptions],
    );

    // (zstanik): debounce search changes to another, throttled state variable that can be used for
    // the API calls.
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const [throttledSearchValue, setThrottledSearchValue] = useState<string | undefined>(undefined);

    const throttledSetSearchValue = useCallback(() => {
      setThrottledSearchValue(searchValue);
    }, [searchValue]);
    useDebounce(throttledSetSearchValue, DEBOUNCE_TIMEOUT, [searchValue]);

    const fieldsQueryResult = useLatestSpectrumFieldVersionsV2Query({
      variables: {
        search: throttledSearchValue,
        sortBy: CustomFieldDefaultColumnOption.FIELD,
        limit: PAGINATION_LIMIT,
        offset: 0,
        params: {
          excludeControllerFields: true,
          excludeRoleFields: selectedTabId === FieldOrRoleSelectTabId.FIELDS,
          includeOnlyRoleFields: selectedTabId === FieldOrRoleSelectTabId.ROLES,
        },
      },
      // (dosipiuk): needed for `fetchMore` to trigger `loading` state
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
      onError: (error) => {
        handleError(error, {
          toastMessage: t`Unable to fetch data, please try again or contact a Regrello admin if you continue to encounter this problem`,
        });
      },
    });

    const onFieldCreate = useCallback(
      async (newField: SpectrumFieldVersionFields | null) => {
        await fieldsQueryResult.refetch();
        if (newField != null) {
          onChange(newField, "create-option");
        }
      },
      [fieldsQueryResult, onChange],
    );

    const onRoleCreate = useCallback(
      async (role: { spectrumFieldVersion?: SpectrumFieldVersionFields | null }) => {
        await fieldsQueryResult.refetch();
        if (role.spectrumFieldVersion != null) {
          onChange(role.spectrumFieldVersion, "create-option");
        }
      },
      [fieldsQueryResult, onChange],
    );

    const renderTab = useCallback(
      (
        tabId: FieldOrRoleSelectTabId,
        text: React.ReactNode,
        overrideTabNames: RegrelloFormFieldOrRoleSelectProps["overrideTabNames"],
        hiddenTabsMap: typeof hiddenTabs,
      ) => {
        if (hiddenTabsMap[tabId]) {
          return undefined;
        }

        return (
          <RegrelloTabsTrigger
            className="w-full"
            onClick={() => {
              setSelectedTabId(tabId);
            }}
            value={tabId}
          >
            <RegrelloTypography className="uppercase" variant="body-xs" weight="semi-bold">
              {overrideTabNames?.[tabId] ?? text}
            </RegrelloTypography>
          </RegrelloTabsTrigger>
        );
      },
      [],
    );

    const renderedTabs = useMemo(() => {
      return (
        // Show tabs for all field types as custom top content in the popover.
        <div className="flex flex-col mx-1">
          {/* (clewis): Allow the tab underline to overlap the border below. */}
          <div className="relative z-1 -mb-px pt-0 sm:pt-0 flex flex-row justify-stretch gap-1 overflow-x-auto">
            <RegrelloTabsRoot className="p-0 w-full" value={selectedTabId}>
              <RegrelloTabsList className="p-0 w-full">
                {[
                  renderTab(FieldOrRoleSelectTabId.ALL, t`All`, overrideTabNames, hiddenTabs),
                  renderTab(FieldOrRoleSelectTabId.FIELDS, t`Fields`, overrideTabNames, hiddenTabs),
                  renderTab(FieldOrRoleSelectTabId.ROLES, t`Roles`, overrideTabNames, hiddenTabs),
                ]}
              </RegrelloTabsList>
            </RegrelloTabsRoot>
          </div>
          <div className="z-0 border-b border-border" />
        </div>
      );
    }, [renderTab, selectedTabId, overrideTabNames, hiddenTabs]);

    const filterOption = useCallback(
      (field: SpectrumFieldVersionFields) => {
        if (
          allowedFieldPlugins != null &&
          allowedFieldPlugins.length > 0 &&
          !allowedFieldPlugins.some((plugin) => plugin.canProcessSpectrumField(field))
        ) {
          return false;
        }

        if (field.field?.fieldType === FieldType.SYSTEM) {
          if (
            !allowSelectWorkflowOwner ||
            // (akager) Hack: We rely on the field name to determine if it's the workflow owner field.
            field.name !== WORKFLOW_OWNER_FIELD_NAME
          ) {
            return false;
          }
        }

        return !omittedOptionIds.has(field.spectrumField.id);
      },
      [allowSelectWorkflowOwner, allowedFieldPlugins, omittedOptionIds],
    );

    const renderOption = useCallback(
      (option: SpectrumFieldVersionFields | null, isSelected: boolean) => {
        if (option == null || option.id === 0) {
          return;
        }

        if (option.propertyType.dataType === PropertyDataType.REGRELLO_OBJECT_INSTANCE_ID) {
          if (option.field?.regrelloObject == null) {
            return null;
          }

          return <RegrelloFormFieldSelectOptionRegrelloObject regrelloObject={option.field.regrelloObject} />;
        }

        const fieldPlugin = SpectrumFieldPluginRegistrar.getPluginForSpectrumField(option);

        return (
          <RegrelloFormFieldSelectOption
            endAdornment={
              isSelected &&
              formFieldSelectV2Props.isRequiredAsteriskShown && <span className="text-danger-textMuted">*</span>
            }
            mainSnippets={[{ text: option.name, highlight: false }]}
            startAdornment={
              <div className="mr-1">
                <RegrelloIcon
                  iconName={fieldPlugin.getIconName(option.field?.fieldType, option.field ?? undefined)}
                  intent="neutral"
                />
              </div>
            }
          />
        );
      },
      [formFieldSelectV2Props.isRequiredAsteriskShown],
    );

    const renderSelectableOption = useCallback(
      (option: SpectrumFieldVersionFields | null) => {
        return renderOption(option, false);
      },
      [renderOption],
    );

    const renderSelectedOption = useCallback(
      (option: SpectrumFieldVersionFields | null) => {
        return renderOption(option, true);
      },
      [renderOption],
    );

    const handleScrollToBottom = useCallback(
      async ({ currentTarget }: { currentTarget: HTMLElement }) => {
        if (
          // We are not loading
          !fieldsQueryResult.loading &&
          // We have some data
          fieldsQueryResult.data != null &&
          // And there are still fields to be fetched
          fieldsQueryResult.data.latestSpectrumFieldVersionsV2.fields.length !==
            fieldsQueryResult.data.latestSpectrumFieldVersionsV2.totalCount &&
          // When we reach bottom of container
          currentTarget.scrollTop + currentTarget.clientHeight >= currentTarget.scrollHeight
        ) {
          await fieldsQueryResult.fetchMore({
            variables: {
              offset: fieldsQueryResult.data?.latestSpectrumFieldVersionsV2.fields.length,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return prev;
              }
              return {
                latestSpectrumFieldVersionsV2: {
                  totalCount: fetchMoreResult.latestSpectrumFieldVersionsV2.totalCount,
                  fields: [
                    ...prev.latestSpectrumFieldVersionsV2.fields,
                    ...fetchMoreResult.latestSpectrumFieldVersionsV2.fields,
                  ],
                },
              };
            },
          });
        }
      },
      [fieldsQueryResult],
    );

    const extraAddOptions = useMemo(
      () => [
        selectedTabId === FieldOrRoleSelectTabId.ROLES ? (
          <RegrelloSelectV2AddOption
            key="option-add-role"
            allowCreateDisabledText={t`Please contact an admin if you would like to create a new role`}
            allowCreateOptions={allowCreate?.roles}
            iconName="add"
            message={t`Add role`}
            messageWithInput={(inputValue) => {
              const roleName = inputValue.name;

              return (
                <span>
                  <Trans>
                    Add role <span>&apos;</span>
                    {roleName}
                    <span>&apos;</span> to Regrello
                  </Trans>
                </span>
              );
            }}
            onSelect={(inputValue) => {
              roleCreateDialog.open(inputValue);
            }}
            value="__CREATE_NEW_ROLE__"
          />
        ) : (
          <RegrelloSelectV2AddOption
            key="option-add"
            allowCreateOptions={allowCreate?.field}
            iconName="add"
            messageWithInput={(inputValue) => {
              const fieldName = inputValue.name;

              return (
                <span>
                  <Trans>
                    Add field <span>&apos;</span>
                    {fieldName}
                    <span>&apos;</span> to Regrello
                  </Trans>
                </span>
              );
            }}
            onSelect={(inputValue) => {
              fieldCreateDialog.open(inputValue);
            }}
          />
        ),
        fieldsQueryResult.loading ? <RegrelloSelectV2LoadingOption key="option-loading" /> : undefined,
      ],
      [
        allowCreate?.field,
        allowCreate?.roles,
        fieldsQueryResult.loading,
        fieldCreateDialog,
        roleCreateDialog,
        selectedTabId,
      ],
    );

    return (
      <>
        <RegrelloFormFieldSelectV2
          {...formFieldSelectV2Props}
          className="flex-auto" // HACKHACK (clewis): Fix full width.
          extraEndOptions={extraAddOptions}
          filterOption={filterOption}
          getOptionLabel={getFieldLabel}
          getOptionValue={getFieldValue}
          isLoading={fieldsQueryResult.data == null && fieldsQueryResult.loading}
          isServerFiltered={true}
          onChange={onChange}
          onInputValueChange={setSearchValue}
          onScroll={handleScrollToBottom}
          options={fieldsQueryResult.data?.latestSpectrumFieldVersionsV2.fields ?? EMPTY_ARRAY}
          popoverCustomTopContent={renderedTabs}
          renderOption={renderSelectableOption}
          renderSelectedValue={renderSelectedOption}
          triggerButtonProps={{ variant }}
          value={propsValue}
        />
        {fieldCreateDialog.isOpen ? (
          <ConfigureSpectrumFieldDialog
            allowedFieldPlugins={allowedFieldPlugins}
            defaultValues={{
              name: fieldCreateDialog.payload.current || "",
              allowedValues: [],
              helperText: "",
              pluginUri: "",
              propertyDataType: undefined,
              valueConstraints: [],
              valueConstraintsComplex: {},
            }}
            isOpen={true}
            onAfterCreation={onFieldCreate}
            onClose={fieldCreateDialog.close}
          />
        ) : null}
        {roleCreateDialog.isOpen ? (
          <RegrelloAddRoleDialog
            defaultValues={{
              name: roleCreateDialog.payload.current || "",
              allowInvitingPeopleToTheWorkspace: false,
            }}
            isOpen={true}
            onClose={roleCreateDialog.close}
            onRoleAdded={onRoleCreate}
          />
        ) : null}
      </>
    );
  },
);

function getFieldLabel(field: SpectrumFieldVersionFields): string {
  return field.name;
}

function getFieldValue(field: { id: number }) {
  return field.id.toString();
}
